import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { Ticket, Plus, Lifebuoy } from "@phosphor-icons/react";

const STATUS_TONE = {
  open: "bg-blue-50 border-blue-300 text-blue-700",
  in_progress: "bg-amber-50 border-amber-300 text-amber-800",
  resolved: "bg-emerald-50 border-emerald-300 text-emerald-700",
  closed: "bg-zinc-100 border-zinc-300 text-zinc-600",
};

const PRIORITY_TONE = {
  urgent: "bg-red-50 border-red-300 text-red-700",
  high: "bg-orange-50 border-orange-300 text-orange-700",
  normal: "bg-zinc-100 border-zinc-300 text-zinc-700",
  low: "bg-zinc-50 border-zinc-200 text-zinc-500",
};

export default function HelpDesk() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", category: "technical", priority: "normal" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tickets", { params: { status: statusFilter } });
      setTickets(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const create = async () => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Subject and description required"); return; }
    setCreating(true);
    try {
      await api.post("/tickets", form);
      toast.success("Ticket created");
      setForm({ subject: "", body: "", category: "technical", priority: "normal" });
      setShowCreate(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const counts = {
    all: tickets.length,
    open: tickets.filter((t) => t.status === "open").length,
    in_progress: tickets.filter((t) => t.status === "in_progress").length,
    resolved: tickets.filter((t) => t.status === "resolved").length,
    closed: tickets.filter((t) => t.status === "closed").length,
  };

  return (
    <div>
      <PageHeader
        overline="Support"
        title="Help desk"
        subtitle="Report issues, ask for help, and track responses from your institution's support team."
        action={
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800"
            data-testid="ticket-new-btn"
          >
            <Plus size={14} /> {showCreate ? "Cancel" : "New ticket"}
          </button>
        }
      />

      {showCreate && (
        <div className="border border-zinc-200 p-6 mb-6" data-testid="ticket-create-form">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Open a new ticket</div>
          <input
            placeholder="Subject (short summary)"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none mb-3"
            data-testid="ticket-subject-input"
          />
          <div className="grid grid-cols-2 gap-3 mb-3">
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none bg-white"
              data-testid="ticket-category-select"
            >
              <option value="technical">Technical</option>
              <option value="billing">Billing</option>
              <option value="exam">Exam</option>
              <option value="account">Account</option>
              <option value="other">Other</option>
            </select>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none bg-white"
              data-testid="ticket-priority-select"
            >
              <option value="low">Low priority</option>
              <option value="normal">Normal priority</option>
              <option value="high">High priority</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <textarea
            placeholder="Describe your issue in detail…"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            rows={5}
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none mb-3"
            data-testid="ticket-body-input"
          />
          <button
            onClick={create}
            disabled={creating}
            className="bg-zinc-900 text-white px-5 py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
            data-testid="ticket-submit-btn"
          >
            {creating ? "Creating…" : "Create ticket"}
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="ticket-filters">
        {["all", "open", "in_progress", "resolved", "closed"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${statusFilter === s ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-700 hover:border-zinc-900"}`}
            data-testid={`ticket-filter-${s}`}
          >
            {s.replace("_", " ")} · {counts[s]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : tickets.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center" data-testid="ticket-empty">
          <Ticket size={40} className="mx-auto text-zinc-300 mb-3" />
          <div className="text-sm text-zinc-500">No tickets to show.</div>
        </div>
      ) : (
        <div className="border border-zinc-200 divide-y divide-zinc-200" data-testid="ticket-list">
          {tickets.map((t) => (
            <Link
              to={`/app/tickets/${t.ticket_id}`}
              key={t.ticket_id}
              className="flex items-start justify-between gap-4 p-5 hover:bg-zinc-50"
              data-testid={`ticket-row-${t.ticket_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <div className="font-medium">{t.subject}</div>
                  <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 ${STATUS_TONE[t.status]}`}>{t.status?.replace("_", " ")}</span>
                  <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 ${PRIORITY_TONE[t.priority]}`}>{t.priority}</span>
                  <span className="text-[10px] uppercase tracking-wider border border-zinc-300 px-2 py-0.5">{t.category}</span>
                </div>
                <div className="text-xs text-zinc-500 line-clamp-1">{t.body}</div>
                <div className="text-[11px] text-zinc-500 mt-2">
                  {t.user_name} · {new Date(t.updated_at).toLocaleString()} · {t.message_count || 0} messages
                </div>
              </div>
              <div className="shrink-0 text-zinc-300">
                <Lifebuoy size={20} weight="light" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
